import {
  ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, HostListener, inject, input, signal,
  untracked, ViewEncapsulation
} from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { SIGNAGE_PROFILE } from '../../runtime/profile';
import { SignageRuntimeService } from '../../runtime/signage-runtime.service';
import { ObserveHeightChangesDirective } from '../../shared/directives/observe-height-changes.directive';
import { LocalStorageKey } from '../../shared/enums';
import { SignageFooterComponent } from '../footer/signage-footer.component';
import { ConfigurationState } from '../../shared/interfaces';
import { SlideHostComponent } from '../slide-host/slide-host.component';
import { SlideLayoutConfigService } from '../slide-layout-config.service';
import { accentChannels, onAccent } from '../theming/accent-contrast';
import { themeAttribute } from '../theming/theme-attribute';

const PREVIEW_ASPECT_RATIO = 16 / 9;
const DEFAULT_ACCENT = '#808080';

@Component({
  selector: 'signage-display',
  imports: [ObserveHeightChangesDirective, SlideHostComponent, SignageFooterComponent],
  templateUrl: './signage-display.component.html',
  styleUrls: ['./signage-display.component.scss'],
  providers: [SlideLayoutConfigService],
  host: {
    '[attr.data-ds-theme]': 'theme()',
    '[style.--ds-color-accent]': 'accent()',
    '[style.--ds-color-on-accent]': 'accentInk()',
    '[style.--ds-color-accent-rgb]': 'accentRgb()'
  },
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SignageDisplayComponent {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly runtime = inject(SignageRuntimeService);
  private readonly translate = inject(TranslateService);
  private readonly profile = inject(SIGNAGE_PROFILE);
  private readonly started = signal(false);
  private readonly community = computed(() => this.runtime.configuration()?.community);
  // Bound on the host, like --height-ds: a preview must not restyle the page around it.
  public readonly theme = computed(() => themeAttribute(this.runtime.configuration()?.raw.themeStyle));
  public readonly accent = computed(() => this.runtime.configuration()?.raw.primaryColor ?? DEFAULT_ACCENT);
  public readonly accentInk = computed(() => onAccent(this.accent()));
  public readonly accentRgb = computed(() => accentChannels(this.accent()));
  private appliedCulture?: string;

  /** Supplied by hosts that assemble the configuration themselves; display hosts leave it unset. */
  public readonly configuration = input<ConfigurationState | undefined>(undefined);
  public readonly culture = input<string | undefined>(undefined);
  public readonly manualNavigation = input(false);

  public constructor() {
    effect(() => {
      const configuration = this.configuration();
      untracked(() => this.restartWith(configuration));
    });
    effect(() => {
      const community = this.community();
      const initial = this.culture();
      untracked(() => this.applyLocale(community?.culture ?? initial, community?.timezone));
    });
    inject(DestroyRef).onDestroy(() => this.runtime.stop());
  }

  @HostListener('window:keydown', ['$event'])
  public onKeyDown(event: KeyboardEvent): void {
    if (!this.manualNavigation()) {
      return;
    }

    if (event.key === 'ArrowLeft') {
      this.runtime.advance(-1);
    } else if (event.key === 'ArrowRight') {
      this.runtime.advance(1);
    }
  }

  public onSizeChange(event: { width: number; height: number }): void {
    // The preview is clamped to 16:9 so display-only styling cannot break the surrounding page.
    const height = this.profile === 'preview'
      ? Math.min(event.height, event.width / PREVIEW_ASPECT_RATIO) : event.height;

    // Set on this host, not the document: everything that reads it lives inside, and a preview must not restyle its page.
    this.host.nativeElement.style.setProperty('--height-ds', `${height}px`);
  }

  private restartWith(configuration?: ConfigurationState): void {
    if (!this.started()) {
      this.started.set(true);
      void this.runtime.start(configuration);

      return;
    }

    if (configuration !== undefined) {
      void this.runtime.setConfiguration(configuration);
    }
  }

  // The header interceptor and the footer both read the community locale back out of local storage.
  private applyLocale(culture?: string, timezone?: string): void {
    if (timezone) {
      localStorage.setItem(LocalStorageKey.DSTimezone, timezone);
    }

    if (!culture || culture === this.appliedCulture) {
      return;
    }

    this.appliedCulture = culture;
    localStorage.setItem(LocalStorageKey.DSLanguage, culture);
    this.translate.use(culture);
  }
}
